/**
 * Доменные типы агента ОСНОВА.МТО (материально-техническое обеспечение).
 *
 * Каркас (этап 0): зеркало DTO роутов `/api/v1/agents/mto/*`. Описывают
 * карточку агента (`/info`) и метрики-заглушки (`/metrics`). Бизнес-логики
 * пока нет — только структура витрины. snake_case 1:1 с backend.
 *
 * Когда появятся модули с реальными сущностями (потребность ТМЦ, номенклатура
 * 1С, паспорта качества), их типы добавляются сюда же, рядом с карточкой.
 */

/** Статус модуля агента. На каркасе все модули в разработке (`planned`);
 *  union держим открытым к будущим состояниям (`active` и т.п.). */
export type MtoModuleStatus = 'planned' | 'active';

/** Один модуль агента ОСНОВА.МТО (витрина в дашборде). */
export interface MtoModule {
  /** Код модуля (стабильный идентификатор, латиница). */
  code: string;
  /** Человекочитаемое название модуля. */
  title: string;
  /** Текущий статус модуля. На каркасе — `planned`. */
  status: MtoModuleStatus;
}

/** Карточка агента ОСНОВА.МТО (каркас): описание + перечень модулей. */
export interface MtoInfo {
  agent_code: string;
  title: string;
  description: string;
  /** Перечень модулей агента (на каркасе — 6 модулей со статусом `planned`). */
  modules: MtoModule[];
}

/** Метрики-заглушки агента ОСНОВА.МТО (`/metrics`). На каркасе — нули. */
export interface MtoMetrics {
  /** Всего прогонов агента. */
  runs_total: number;
  /** Всего обработанных паспортов качества. */
  passports_total: number;
  /** Позиций в нормализованной номенклатуре 1С. */
  nomenclature_total: number;
  /** Открытых расхождений по снабжению (требуют внимания). */
  open_discrepancies: number;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Модуль 1.1 «Паспорта качества на песок».
 *
 * Снабженец загружает выгрузку реализаций 1С (отгрузки песка партиями) и реестр
 * протоколов испытаний по ГОСТ 8736; агент сопоставляет партии с протоколами,
 * собирает паспорта качества (DOCX) и подсвечивает расхождения (аномалии).
 * Результат проходит HITL-ревью на уровне прогона, паспорта и аномалии.
 *
 * Типы — зеркало DTO `/agents/mto/*`, snake_case 1:1 с backend. Числовые
 * количества и показатели backend отдаёт СТРОКОЙ (Decimal не теряет точность
 * при сериализации) — на фронте принимаем как `string`, не приводим к number.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Статус прогона. `created`/`processing` — активные (имеет смысл polling). */
export type MtoRunStatus = 'created' | 'processing' | 'completed' | 'failed';

/** Статус паспорта: черновик (собран агентом) → подтверждён/отклонён в HITL. */
export type MtoPassportStatus = 'draft' | 'confirmed' | 'rejected';

/** Результат сопоставления партии реализации с протоколом испытаний. */
export type MtoMatchStatus = 'matched' | 'ambiguous' | 'unmatched';

/** Критичность аномалии (как в econ — единая шкала для тона Badge). */
export type MtoSeverity = 'critical' | 'major' | 'minor';

/** Тон Badge — тот же набор, что в `@entities/economic/types` (UI-примитив
 *  `@shared/ui/Badge` принимает строго эти значения). */
export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

/** Прогон агента МТО. Числа статуса/прогресса — number (метаданные пайплайна);
 *  предметные количества живут в `MtoPassport.quantity`/`indicators` строками. */
export interface MtoRun {
  id: string;
  user_id: string | null;
  /** Код модуля прогона (для 1.1 — паспорта качества). Backend отдаёт строкой. */
  module: string | null;
  status: MtoRunStatus;
  /** Прогресс пайплайна 0..100 (метаданные, не предметное число). */
  progress_percent: number;
  summary: string | null;
  error_message: string | null;
  /** Сырые данные прогона (kind-зависимый JSON) — на витрине не показываем. */
  raw_data: unknown;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  /** Решение HITL по прогону (`accepted`/`rejected`/`disputed`) или `null`. */
  decision: string | null;
  decided_by: string | null;
  decided_at: string | null;
  content_hash: string | null;
}

/** Собранный паспорт качества на партию песка. Количество и показатели —
 *  строки (Decimal с backend), не number. */
export interface MtoPassport {
  id: string;
  run_id: string;
  status: MtoPassportStatus;
  /** Сопоставление партии реализации с протоколом испытаний. */
  match_status: MtoMatchStatus;
  realization_id: string | null;
  protocol_id: string | null;
  passport_no: string | null;
  passport_date: string | null;
  buyer: string | null;
  material_name: string | null;
  quarry: string | null;
  batch_no: string | null;
  /** Отгруженное количество (Decimal-строка). */
  quantity: string | null;
  unit: string | null;
  /** Показатели качества: код показателя → значение-строка. Подписи — через
   *  [`labelIndicator`]. Неизвестный код показываем как есть. */
  indicators: Record<string, string>;
  /** Решение HITL по паспорту (`accepted`/`rejected`) или `null`. */
  decision: string | null;
  decided_by: string | null;
  decided_at: string | null;
  created_at: string;
  /** Сформирован ли DOCX-файл паспорта (доступно скачивание). */
  has_document: boolean;
}

/** Аномалия прогона МТО (расхождение, требующее внимания). */
export interface MtoAnomaly {
  id: string;
  run_id: string;
  kind: string;
  severity: MtoSeverity;
  title: string;
  description: string | null;
  /** Решение HITL по аномалии (`acknowledged`/`rejected`) или `null`. */
  decision: string | null;
  /** Связанные сущности (kind-зависимый JSON). */
  related: unknown;
  /** Находка не подтверждена (сопоставлено по похожему наименованию) — в
   *  служебную записку не идёт. */
  provisional: boolean;
  created_at: string;
}

/**
 * Позиция потребности ТМЦ, извлечённая из РД (модуль 1.2 «Потребность из РД»).
 * Зеркало DTO `materials` карточки прогона. Количество — СТРОКА (Decimal, не
 * теряем точность); `confidence` — 0..1 уверенность извлечения; `needs_review`
 * — позиция требует ручной проверки (подсвечиваем в таблице). HITL по позиции —
 * через [`MtoMaterialDecision`].
 */
export interface MtoMaterialPosition {
  id: string;
  run_id: string;
  record_uid: string;
  /** Роль источника (`rd` — текст РД, `rd_table` — таблица спецификации и т.п.).
   *  Подпись — через [`materialSourceLabel`]. */
  source_role: string;
  name: string;
  /** ГОСТ/ТУ позиции. */
  gost: string | null;
  /** Шифр/код позиции. */
  code: string | null;
  /** Марка/обозначение. */
  designation: string | null;
  unit: string | null;
  /** Количество (Decimal-строка), не number. */
  quantity: string | null;
  section: string | null;
  /** Ссылка на источник (лист/раздел документа). */
  source_ref: string;
  /** Номер страницы источника (0 — нет привязки к странице). */
  page_number: number;
  /** Уверенность извлечения 0..1 или `null`. Формат — [`fmtConfidence`]. */
  confidence: number | null;
  needs_review: boolean;
  /** Решение HITL по позиции (`accepted`/`rejected`/`disputed`) или `null`. */
  decision: string | null;
  decided_by: string | null;
  decided_at: string | null;
  /** Сырые данные позиции (kind-зависимый JSON) — на витрине не показываем. */
  raw_data: unknown;
  created_at: string;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Модуль 1.3 «НСИ-сопоставление» (нормализация потребности к справочнику).
 *
 * После извлечения потребности (1.2) каждая позиция нормализуется к справочнику
 * номенклатуры (НСИ): точное/семантическое/текстовое совпадение либо «не найдено».
 * Для неоднозначных совпадений агент предлагает кандидатов с уверенностью —
 * снабженец выбирает верный и подтверждает (это пополняет словарь синонимов).
 *
 * Отдельной страницы списка у модуля нет: сопоставление идёт в составе прогона
 * `rd_extract` (карточка прогона). Справочник НСИ управляется на дашборде раздела.
 *
 * Типы — зеркало DTO `mappings` карточки прогона, snake_case 1:1 с backend.
 * `confidence` — 0..1 (формат — [`fmtConfidence`]); количества здесь нет.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Тип совпадения позиции потребности со справочником НСИ. `none` — кандидат
 *  не найден (позиция нормализована не была). Подпись — [`matchTypeLabel`]. */
export type NsiMatchType = 'exact' | 'fuzzy' | 'text' | 'none';

/** Статус сопоставления: `auto` — проставлено агентом без ревью, `verified` —
 *  подтверждено снабженцем, `pending` — вынесено на проверку, `rejected` —
 *  отклонено. Подпись/тон — [`mappingStatusLabel`]/[`mappingStatusTone`]. */
export type NsiMappingStatus = 'auto' | 'verified' | 'rejected' | 'pending';

/** Позиция справочника материалов для просмотра/поиска (зеркало
 *  `NomenclatureListItem`). Лёгкая строка без эмбеддинга/синонимов. */
export interface NomenclatureItem {
  id: string;
  code: string | null;
  name: string;
  unit: string | null;
  category: string | null;
}

/** Кандидат из справочника НСИ для позиции потребности. `confidence` — 0..1. */
export interface NsiCandidate {
  /** Идентификатор позиции справочника НСИ. */
  nomenclature_id: string;
  /** Код номенклатуры (артикул/шифр) или `null`. */
  code: string | null;
  /** Наименование позиции справочника. */
  name: string;
  /** Единица измерения справочной позиции или `null`. */
  unit: string | null;
  /** Уверенность совпадения 0..1 (формат — [`fmtConfidence`]). */
  confidence: number;
}

/** Сопоставление позиции потребности (1.2) со справочником НСИ (1.3). Привязка —
 *  через `nomenclature_id` (выбранный кандидат) либо `null` (не привязано). */
export interface NsiMapping {
  id: string;
  run_id: string;
  /** Позиция потребности (1.2), к которой относится сопоставление, или `null`. */
  material_position_id: string | null;
  /** Исходный текст позиции (как пришло из РД) — что сопоставляем. */
  source_text: string;
  /** Привязанная позиция справочника НСИ или `null` (ещё не привязано). */
  nomenclature_id: string | null;
  /** Тип совпадения. Подпись — [`matchTypeLabel`]. */
  match_type: NsiMatchType;
  /** Уверенность сопоставления 0..1 или `null`. Формат — [`fmtConfidence`]. */
  confidence: number | null;
  /** Статус сопоставления. Тон/подпись — [`mappingStatusTone`]/[`mappingStatusLabel`]. */
  status: NsiMappingStatus;
  /** Кандидаты справочника НСИ (для уточнения неоднозначного совпадения). */
  candidates: NsiCandidate[];
  verified_by: string | null;
  verified_at: string | null;
  created_at: string;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Модуль 2.1 «Контроль снабжения (план-факт поставок)».
 *
 * Снабженец загружает заказы на закупку (план) и приходы/поступления (факт);
 * агент сверяет план с фактом по позициям и подсвечивает просрочки и
 * недопоставки (аномалии: kind `overdue`/`overdue_partial`/`undersupply`/
 * `delivery_without_order`, severity critical/major/minor — общий блок «Аномалии»).
 *
 * Типы — зеркало DTO `orders`/`deliveries` карточки прогона, snake_case 1:1 с
 * backend. Количества — СТРОКА (Decimal, не теряем точность), даты — строки.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Заказ на закупку (план поставки), извлечённый из документа заказов. Количество
 *  — строка (Decimal), не number; `due_date` — плановый срок поставки. */
export interface MtoPurchaseOrder {
  id: string;
  run_id: string;
  record_uid: string;
  /** Номер заказа на закупку или `null`. */
  order_no: string | null;
  /** Номер договора, по которому идёт закупка, или `null`. */
  contract_no: string | null;
  /** Поставщик или `null`. */
  supplier: string | null;
  position_name: string;
  /** Заказанное количество (Decimal-строка), не number. */
  quantity: string | null;
  unit: string | null;
  /** Плановый срок поставки (`YYYY-MM-DD`) или `null`. */
  due_date: string | null;
  /** Ссылка на источник (лист/строка документа). */
  source_ref: string;
  /** Имя исходного файла или `null`. */
  source_file: string | null;
  /** Сырые данные строки (kind-зависимый JSON) — на витрине не показываем. */
  raw_data: unknown;
  created_at: string;
}

/** Приход (факт поставки), извлечённый из приходного документа. Привязка к плану —
 *  через `order_no`; количество — строка (Decimal), не number. */
export interface MtoDelivery {
  id: string;
  run_id: string;
  record_uid: string;
  /** Номер приходного документа/поступления или `null`. */
  delivery_no: string | null;
  /** Номер заказа, к которому относится приход (привязка факта к плану), или `null`. */
  order_no: string | null;
  position_name: string;
  /** Поступившее количество (Decimal-строка), не number. */
  quantity: string | null;
  unit: string | null;
  /** Дата прихода (`YYYY-MM-DD`) или `null`. */
  delivery_date: string | null;
  /** Ссылка на источник (лист/строка документа). */
  source_ref: string;
  /** Имя исходного файла или `null`. */
  source_file: string | null;
  /** Сырые данные строки (kind-зависимый JSON) — на витрине не показываем. */
  raw_data: unknown;
  created_at: string;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Модуль 2.2 «Long-lead (длинноцикловые позиции)».
 *
 * Снабженец загружает реестр длинноцикловых позиций (с длительным сроком
 * поставки); агент рассчитывает точку заказа (`order_by_date`) с учётом срока
 * поставки и буфера и подсвечивает просрочки и приближение точки заказа
 * (аномалии: kind `longlead_overdue`/`longlead_due_soon`, severity critical/major
 * — общий блок «Аномалии»). Позиции с `ordered=false` ждут запуска закупки.
 *
 * Типы — зеркало DTO `longlead` карточки прогона, snake_case 1:1 с backend.
 * Сроки в днях и даты — СТРОКИ (Decimal/дата, не теряем точность), не number.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Длинноцикловая позиция (модуль 2.2): срок поставки + буфер дают точку заказа.
 *  Сроки в днях (`lead_time_days`/`buffer_days`) backend отдаёт СТРОКОЙ (Decimal),
 *  не number; `ordered` — запущена ли уже закупка. */
export interface MtoLongLeadItem {
  id: string;
  run_id: string;
  record_uid: string;
  position_name: string;
  /** Срок поставки в днях (Decimal-строка) или `null`. */
  lead_time_days: string | null;
  /** Дата вовлечения позиции (когда нужна на объекте), `YYYY-MM-DD` или `null`. */
  required_date: string | null;
  /** Страховой буфер в днях (Decimal-строка) или `null`. */
  buffer_days: string | null;
  /** Запущена ли закупка позиции. */
  ordered: boolean;
  /** Точка заказа — крайняя дата запуска закупки (`YYYY-MM-DD`) или `null`. */
  order_by_date: string | null;
  /** Ссылка на источник (лист/строка документа). */
  source_ref: string;
  /** Имя исходного файла или `null`. */
  source_file: string | null;
  /** Сырые данные строки (kind-зависимый JSON) — на витрине не показываем. */
  raw_data: unknown;
  created_at: string;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Модуль 2.3 «Реестр поставщиков и база КП».
 *
 * Снабженец загружает коммерческие предложения (КП) поставщиков; агент извлекает
 * позиции с ценами, регистрирует поставщика в реестре и привязывает каждую
 * позицию к номенклатуре НСИ (точное → семантическое совпадение). offers с
 * `created_at` копятся как история цен (сырьё для будущего прогноза цен).
 *
 * Типы — зеркало DTO `offers` карточки прогона, snake_case 1:1 с backend. Цены и
 * количества — СТРОКА (перенос как есть, без float); даты/сроки — строки. У
 * модуля нет собственных аномалий (привязка к НСИ — информативная).
 * ──────────────────────────────────────────────────────────────────────────── */

/** Поставщик в реестре (модуль 2.3). Растёт по мере загрузки КП — уникален по
 *  имени; `inn`/`contact` опциональны (заполняются при наличии в источнике). */
export interface MtoSupplier {
  id: string;
  name: string;
  /** ИНН поставщика или `null`. */
  inn: string | null;
  /** Контакты (телефон/почта/менеджер) или `null`. */
  contact: string | null;
  created_at: string;
}

/** Реквизиты поставщика сверх ИНН и контактов — из реестра снабженца. */
export interface MtoSupplierDetails {
  kpp?: string;
  phone?: string;
  email?: string;
  person?: string;
  specialization?: string | null;
  address?: string;
  site?: string;
  note?: string;
  /** Не похоже на ИНН — как было в файле, на правку. */
  inn_raw?: string;
  registry_file?: string;
}

/** Поставщик реестра со сводкой по его КП. */
export interface MtoSupplierRegistryRow extends MtoSupplier {
  raw_data: MtoSupplierDetails;
  /** Строк КП поставщика в базе. */
  offers: number;
  last_offer_at: string | null;
}

/** Итог загрузки реестра поставщиков. */
export interface MtoRegistryImport {
  rows: number;
  created: number;
  updated: number;
  skipped: number;
}

/** Позиция коммерческого предложения (модуль 2.3): цена поставщика на позицию.
 *  Цена/количество — СТРОКА (перенос как есть), не number; `valid_until` — срок
 *  действия КП строкой; привязка к НСИ — через `nomenclature_id` (если найдена)
 *  с уверенностью `match_confidence` (0..1). */
export interface MtoSupplierOffer {
  id: string;
  run_id: string;
  record_uid: string;
  /** Поставщик из реестра (если зарегистрирован) или `null`. */
  supplier_id: string | null;
  /** Денормализованное имя поставщика (для показа без JOIN) или `null`. */
  supplier_name: string | null;
  position_name: string;
  /** Цена за единицу (строка, перенос как есть) или `null`. */
  price: string | null;
  /** Валюта цены или `null`. */
  currency: string | null;
  unit: string | null;
  /** Количество в предложении (строка) или `null`. */
  quantity: string | null;
  /** Срок действия предложения (строка/дата как есть) или `null`. */
  valid_until: string | null;
  /** Привязанная позиция справочника НСИ или `null` (не сопоставлено). */
  nomenclature_id: string | null;
  /** Уверенность привязки к НСИ 0..1 или `null`. Формат — [`fmtConfidence`]. */
  match_confidence: number | null;
  /** Цена, разобранная в число (кэш разбора на backend). `null` — не разобрана
   *  («по запросу», диапазон, мусор из OCR); такая позиция в сравнении цен не
   *  участвует. Для показа берём `price` — он перенос 1:1. */
  price_value: number | null;
  /** Класс единицы для сравнения цен («т», «шт», «м3») или `null`. */
  unit_class: string | null;
  /** Как получена привязка к НСИ: `auto` (пороги матчера) | `manual` (человек
   *  привязал руками) | `null` (привязки нет). */
  match_source: string | null;
  /** Ссылка на источник (файл/строка). */
  source_ref: string;
  /** Имя исходного файла или `null`. */
  source_file: string | null;
  /** Сырые данные строки (kind-зависимый JSON) — на витрине не показываем. */
  raw_data: unknown;
  created_at: string;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Сравнение КП (модуль 2.3, этап 2).
 *
 * Строка таблицы — позиция НСИ, ячейки — предложения РАЗНЫХ поставщиков по ней,
 * собранные не из одного прогона, а из всей базы КП за окно (по умолчанию
 * полгода). Отсюда история цен: одна номенклатура видна по нескольким датам.
 *
 * Типы — зеркало DTO backend (`osnova-mto::modules::comparison`), snake_case 1:1.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Предложение в ячейке сравнения. */
export interface ComparisonOffer {
  offer_id: string;
  run_id: string;
  supplier_id: string | null;
  supplier_name: string | null;
  /** Наименование позиции так, как оно написано в КП (написания расходятся). */
  position_name: string;
  /** Цена как в документе (перенос 1:1) — её и показываем. */
  price: string | null;
  /** Цена числом; `null` — не разобрана. */
  price_value: number | null;
  currency: string | null;
  unit: string | null;
  valid_until: string | null;
  source_ref: string;
  created_at: string;
  /** Участвует в минимуме и разбросе. `false` — цена не разобрана либо единица
   *  или валюта не совпали с базой сравнения по позиции. */
  comparable: boolean;
  /** Срок действия КП прошёл: в минимум не входит, пока по позиции есть
   *  действующие предложения. */
  expired: boolean;
  /** Минимальная цена по позиции среди сопоставимых. */
  is_min: boolean;
}

/** Зафиксированный выбор поставщика по позиции (история решений: актуальное —
 *  последнее). Хранится по номенклатуре, а не по строке КП: строки
 *  пересоздаются при переобработке прогона. */
export interface MtoOfferSelection {
  id: string;
  nomenclature_id: string;
  supplier_id: string | null;
  supplier_name: string | null;
  offer_id: string | null;
  run_id: string | null;
  /** Цена на момент решения (текстом 1:1). */
  price: string | null;
  price_value: number | null;
  currency: string | null;
  unit: string | null;
  /** Основание выбора (почему этот поставщик) или `null`. */
  reason: string | null;
  selected_by: string | null;
  selected_at: string;
}

/** Строка сравнения — одна позиция НСИ. */
export interface ComparisonRow {
  nomenclature_id: string;
  nomenclature_name: string;
  nomenclature_unit: string | null;
  /** Класс единицы, в котором сравниваются цены (преобладающий по позиции). */
  compare_unit_class: string | null;
  /** Валюта сравнения (преобладающая); `null` — валюта нигде не указана. */
  compare_currency: string | null;
  offers: ComparisonOffer[];
  min_price_value: number | null;
  max_price_value: number | null;
  /** Разброс цен `(max - min) / min * 100`, проценты. */
  spread_percent: number | null;
  /** Изменение минимальной цены между половинами окна, проценты. `null` — в
   *  одной из половин нет сопоставимых предложений. */
  trend_percent: number | null;
  /** Сколько предложений по позиции в сравнение не попало. */
  incomparable_count: number;
  /** Актуальное решение по позиции или `null`. */
  selection: MtoOfferSelection | null;
}

/** Позиция КП без привязки к НСИ — сравнивать не с чем, пока не привязана. */
export interface UnmatchedOffer {
  offer_id: string;
  run_id: string;
  position_name: string;
  supplier_name: string | null;
  price: string | null;
  unit: string | null;
  source_ref: string;
  created_at: string;
}

/** Позиции разных КП с одной маркой изделия, ещё без справочника. Цены
 *  сравниваются так же, как в строке справочника; выбрать поставщика можно после
 *  «Добавить в справочник». */
export interface ComparisonGroup {
  /** Марка, по которой сведены позиции. */
  key: string;
  /** Самое короткое написание позиции — подпись и наименование для справочника. */
  label: string;
  unit: string | null;
  compare_unit_class: string | null;
  compare_currency: string | null;
  offers: ComparisonOffer[];
  min_price_value: number | null;
  max_price_value: number | null;
  spread_percent: number | null;
  trend_percent: number | null;
  incomparable_count: number;
  /** Разных поставщиков в группе. */
  suppliers: number;
}

/** Итог повторного подбора КП к справочнику. */
export interface MtoRematchReport {
  checked: number;
  matched: number;
  repriced: number;
}

/** Сравнительная таблица целиком. */
export interface MtoComparison {
  rows: ComparisonRow[];
  /** Позиции без справочника, сведённые по марке изделия. */
  groups: ComparisonGroup[];
  unmatched: UnmatchedOffer[];
  /** Левая граница окна выборки (правая — «сейчас»). */
  since: string;
  /** Сколько предложений просмотрено (с несопоставимыми и непривязанными). */
  offers_total: number;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Модуль 2.4 «Остатки и неликвиды».
 *
 * Снабженец загружает выгрузку складских остатков; агент переносит позиции и
 * подсвечивает неликвиды (нет движения дольше горизонта) и отрицательные остатки
 * (ошибка учёта) как аномалии (kind `illiquid`/`negative_stock`, severity major —
 * общий блок «Аномалии»).
 *
 * Типы — зеркало DTO `stock` карточки прогона, snake_case 1:1 с backend.
 * Количества — СТРОКА (перенос как есть), не number; даты — строки.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Позиция складских остатков (модуль 2.4). Остаток — СТРОКА (перенос как есть),
 *  не number; `last_movement_date` — дата последнего движения строкой (для
 *  определения неликвида backend парсит её сам). */
export interface MtoStockItem {
  id: string;
  run_id: string;
  record_uid: string;
  position_name: string;
  /** Остаток (строка, перенос как есть) или `null`. */
  quantity: string | null;
  unit: string | null;
  /** Дата последнего движения (строка/дата как есть) или `null`. */
  last_movement_date: string | null;
  /** Склад/место хранения или `null`. */
  warehouse: string | null;
  /** Ссылка на источник (файл/строка). */
  source_ref: string;
  /** Имя исходного файла или `null`. */
  source_file: string | null;
  /** Сырые данные строки (kind-зависимый JSON) — на витрине не показываем. */
  raw_data: unknown;
  created_at: string;
}

/** Карточка прогона: прогон + собранные паспорта (1.1) + позиции потребности
 *  (1.2) + сопоставления с НСИ (1.3) + заказы/приходы (2.1) + длинноцикловые
 *  позиции (2.2) + предложения поставщиков (2.3) + складские остатки (2.4) +
 *  аномалии. Для passports-прогона `materials=[]`/`mappings=[]`; для прогонов без
 *  сопоставлений — `mappings=[]`; для не-supply прогонов — `orders=[]`/
 *  `deliveries=[]`; для не-long_lead — `longlead=[]`; для не-suppliers —
 *  `offers=[]`; для не-stock — `stock=[]`. */
export interface MtoRunDetail {
  run: MtoRun;
  passports: MtoPassport[];
  materials: MtoMaterialPosition[];
  mappings: NsiMapping[];
  orders: MtoPurchaseOrder[];
  deliveries: MtoDelivery[];
  longlead: MtoLongLeadItem[];
  offers: MtoSupplierOffer[];
  stock: MtoStockItem[];
  anomalies: MtoAnomaly[];
}

/** Решение HITL по прогону. 409 от backend — если есть неразобранные
 *  критические аномалии (показываем текст ошибки). */
export type MtoRunDecision = 'accepted' | 'rejected' | 'disputed';

/** Решение HITL по паспорту качества. */
export type MtoPassportDecision = 'accepted' | 'rejected';

/** Решение HITL по аномалии. */
export type MtoAnomalyDecision = 'acknowledged' | 'rejected';

/** Решение HITL по позиции потребности ТМЦ (модуль 1.2). `reset` — не
 *  решение, а команда снять прежнее (вернуть убранную строку). */
export type MtoMaterialDecision = 'accepted' | 'rejected' | 'disputed' | 'reset';

/** Решение HITL по сопоставлению с НСИ (модуль 1.3). `verified` привязывает
 *  выбранного кандидата (требует `nomenclature_id`), `rejected` — отклоняет
 *  сопоставление, `pending` — возвращает на проверку. */
export type MtoMappingDecision = 'verified' | 'rejected' | 'pending';

/* ── Конфигурация загрузки документов модуля 1.1 ───────────────────────────── */

/** Один тип входного документа прогона. Имя поля файла в FormData = `docType`
 *  (контракт createMtoRun: backend читает doc_type из имени поля multipart).
 *  `realizations`/`test_protocols` — модуль 1.1 «Паспорта качества»;
 *  `rd` — модуль 1.2 «Потребность ТМЦ из РД» (спецификации/ведомости). */
export interface MtoDocType {
  /** Значение поля FormData (несёт doc_type на backend). Канонический литерал.
   *  `realizations`/`test_protocols` — модуль 1.1; `rd` — модуль 1.2; пара
   *  `rd`+`request` — модуль 1.4 «Сверка потребности РД ↔ заявки»; пара
   *  `orders`+`deliveries` — модуль 2.1 «Контроль снабжения (план-факт)»;
   *  `longlead` — модуль 2.2 «Длинноцикловые позиции». */
  docType:
    | 'realizations'
    | 'test_protocols'
    | 'rd'
    | 'request'
    | 'orders'
    | 'deliveries'
    | 'longlead'
    | 'offer'
    | 'stock';
  /** Человекочитаемая подпись поля загрузки. */
  label: string;
  /** Подсказка под полем — что именно приложить. Формат файла не ограничиваем:
   *  агент принимает любой (Excel/CSV, PDF, скан, фото, DOCX) — извлечение само
   *  разберётся. */
  hint: string;
  /** Допустима ли множественная загрузка этого типа в одном прогоне. */
  multiple: boolean;
}

/** Документы прогона «Паспорта качества на песок». Оба типа — таблицы;
 *  оба допускают несколько файлов (несколько выгрузок/реестров за период). */
export const MTO_PASSPORT_DOCTYPES: readonly MtoDocType[] = [
  {
    docType: 'realizations',
    label: 'Выгрузка реализаций 1С',
    hint: 'Отгрузки песка покупателям (партии). Любой формат — Excel/CSV, PDF, скан или фото.',
    multiple: true,
  },
  {
    docType: 'test_protocols',
    label: 'Реестр протоколов испытаний',
    hint: 'Протоколы качества по ГОСТ 8736 (показатели партий). Любой формат — Excel, PDF, скан или фото.',
    multiple: true,
  },
] as const;

/** Документы прогона «Потребность ТМЦ из РД» (модуль 1.2). Один тип входа —
 *  рабочая документация (спецификации/ведомости материалов); допускает
 *  несколько файлов (комплект РД). Файлы уходят под именем поля = `rd`. */
export const MTO_RD_DOCTYPES: readonly MtoDocType[] = [
  {
    docType: 'rd',
    label: 'Документ РД',
    hint: 'PDF комплекта РД или таблица Excel со спецификацией / ведомостью материалов.',
    multiple: true,
  },
] as const;

/** Документы прогона «Сверка потребности РД ↔ заявки» (модуль 1.4). Два типа
 *  входа: потребность по РД (`rd`) и заявки на закупку (`request`). Агент
 *  сверяет позиции обоих источников и подсвечивает рассогласования (аномалии).
 *  Оба типа допускают несколько файлов; уходят под именем поля = `docType`. */
export const MTO_RECONCILE_DOCTYPES: readonly MtoDocType[] = [
  {
    docType: 'request',
    label: 'Заявка на закупку',
    hint: 'Заявка на закупку ТМЦ. Любой формат — Excel, PDF, скан или фото.',
    multiple: true,
  },
  {
    docType: 'rd',
    label: 'Документ РД (если ведомости нет)',
    hint: 'РД файлом — потребность построится тем же путём, что ведомость. Лучше выбрать готовую ведомость выше.',
    multiple: true,
  },
] as const;

/** Документы прогона «Контроль снабжения (план-факт поставок)» (модуль 2.1). Два
 *  типа входа: заказы на закупку (`orders`, план) и приходы/поступления
 *  (`deliveries`, факт). Агент сверяет план с фактом и подсвечивает просрочки и
 *  недопоставки (аномалии). Оба типа допускают несколько файлов; уходят под
 *  именем поля = `docType`. */
export const MTO_SUPPLY_DOCTYPES: readonly MtoDocType[] = [
  {
    docType: 'orders',
    label: 'Заказы на закупку',
    hint: 'План поставок: заказы на закупку ТМЦ. Любой формат — Excel, PDF, скан или фото.',
    multiple: true,
  },
  {
    docType: 'deliveries',
    label: 'Приходы (поступления)',
    hint: 'Факт поставок: приходные документы/поступления. Любой формат — Excel, PDF, скан или фото.',
    multiple: true,
  },
] as const;

/** Документы прогона «Длинноцикловые позиции» (модуль 2.2). Один тип входа —
 *  реестр длинноцикловых позиций (срок поставки, дата вовлечения, буфер); агент
 *  считает точку заказа и подсвечивает просрочки и приближение точки заказа
 *  (аномалии). Допускает несколько файлов; уходят под именем поля = `longlead`. */
export const MTO_LONGLEAD_DOCTYPES: readonly MtoDocType[] = [
  {
    docType: 'longlead',
    label: 'Реестр длинноцикловых позиций',
    hint: 'Позиции с длительным сроком поставки: срок, дата вовлечения, буфер. Любой формат — Excel, PDF, скан или фото.',
    multiple: true,
  },
] as const;

/** Документы прогона «Реестр поставщиков и КП» (модуль 2.3). Один тип входа —
 *  коммерческие предложения поставщиков (позиции с ценами); агент извлекает
 *  цены, регистрирует поставщика и привязывает позиции к НСИ. Допускает несколько
 *  файлов (КП разных поставщиков); уходят под именем поля = `offer`. */
export const MTO_SUPPLIERS_DOCTYPES: readonly MtoDocType[] = [
  {
    docType: 'offer',
    label: 'Коммерческие предложения (КП)',
    hint: 'Прайсы/КП поставщиков с ценами на позиции. Любой формат — Excel, PDF, скан или фото.',
    multiple: true,
  },
] as const;

/** Документы прогона «Остатки и неликвиды» (модуль 2.4). Один тип входа —
 *  выгрузка складских остатков 1С (позиция, остаток, дата последнего движения);
 *  агент подсвечивает неликвиды и отрицательные остатки. Допускает несколько
 *  файлов (выгрузки по складам); уходят под именем поля = `stock`. */
export const MTO_STOCK_DOCTYPES: readonly MtoDocType[] = [
  {
    docType: 'stock',
    label: 'Выгрузка складских остатков',
    hint: 'Остатки и движения из 1С: позиция, остаток, дата последнего движения, склад. Любой формат — Excel/CSV, PDF, скан или фото.',
    multiple: true,
  },
] as const;

/* ── Модули агента ОСНОВА.МТО (выбор перед загрузкой) ───────────────────────── */

/** Код модуля прогона. Канонические литералы поля `module` в FormData createMtoRun
 *  и значения `mto_runs.module` на backend. */
export type MtoModuleId =
  | 'passports'
  | 'rd_extract'
  | 'reconcile'
  | 'supply'
  | 'long_lead'
  | 'suppliers'
  | 'stock';

/** Фаза агента: 1 — формирование потребности, 2 — контроль снабжения. */
export type MtoModulePhase = 1 | 2;

/** Один доступный для запуска модуль. Метаданные в одном месте — рельс навигации,
 *  заголовок раздела, подзаголовок и подсказка по файлам берутся отсюда (раньше
 *  дублировались по страницам). */
export interface MtoModuleOption {
  /** Код модуля (значение поля `module` в FormData и `mto_runs.module`). */
  id: MtoModuleId;
  /** Номер модуля в ТЗ (1.1, 2.3 …) — для рельса и заголовка. */
  number: string;
  /** Фаза агента (для группировки в рельсе навигации). */
  phase: MtoModulePhase;
  /** Человекочитаемое название модуля. */
  title: string;
  /** Описание модуля — подзаголовок рабочего раздела. */
  description: string;
  /** Подсказка под полем загрузки — какие документы приложить. */
  filesHint: string;
}

/** Модули, для которых есть форма запуска обработки. Метаданные в одном месте —
 *  используются в рельсе навигации, заголовке раздела и форме загрузки. Модуль
 *  1.3 «НСИ-сопоставление» отдельной формы не имеет (идёт в составе 1.2). */
export const MTO_MODULES: readonly MtoModuleOption[] = [
  {
    id: 'passports',
    number: '1.1',
    phase: 1,
    title: 'Паспорта качества (песок)',
    description:
      'Загрузка выгрузки реализаций 1С и реестра протоколов испытаний, сопоставление партий с протоколами и сборка паспортов качества (ГОСТ 8736).',
    filesHint:
      'Выгрузка реализаций 1С (отгрузки песка партиями) и реестр протоколов испытаний по ГОСТ 8736. Подойдёт любой формат — Excel/CSV, PDF, скан или фото.',
  },
  {
    id: 'rd_extract',
    number: '1.2',
    phase: 1,
    title: 'Ведомость материалов по РД',
    description:
      'Выберите объект и документы РД — получите ведомость материалов: перечень с количествами и весом, свод арматуры по классам и диаметрам, коды 1С. Выгрузка в Excel.',
    filesHint:
      'PDF документов РД или таблица Excel со спецификацией. Если документ уже есть в библиотеке РД — возьмём готовый разбор ПТО.',
  },
  {
    id: 'reconcile',
    number: '1.4',
    phase: 1,
    title: 'Сверка с заявками',
    description:
      'Сверка потребности по ведомости материалов с заявками на закупку: что не заказано, заказано меньше или больше, что заявлено без потребности.',
    filesHint:
      'Заявки на закупку — Excel, PDF, скан или фото. Потребность берётся из готовой ведомости материалов.',
  },
  {
    id: 'supply',
    number: '2.1',
    phase: 2,
    title: 'Контроль снабжения',
    description:
      'Сверка заказов на закупку с приходами, контроль сроков и объёмов поставок, подсветка просрочек и недопоставок.',
    filesHint:
      'Заказы на закупку (план) и приходы/поступления (факт) — для каждого файла выберите тип. Агент сверит план с фактом и подсветит просрочки и недопоставки. Подойдёт любой формат — Excel, PDF, скан или фото.',
  },
  {
    id: 'long_lead',
    number: '2.2',
    phase: 2,
    title: 'Длинноцикловые позиции',
    description:
      'Реестр длинноцикловых позиций, расчёт точки заказа по сроку поставки и буферу, подсветка просрочек и приближения точки заказа.',
    filesHint:
      'Реестр длинноцикловых позиций: срок поставки, дата вовлечения, буфер. Агент рассчитает точку заказа и подсветит просрочки и приближение точки заказа. Подойдёт любой формат — Excel, PDF, скан или фото.',
  },
  {
    id: 'suppliers',
    number: '2.3',
    phase: 2,
    title: 'Поставщики и КП',
    description:
      'Реестр поставщиков и база коммерческих предложений: извлечение цен, привязка позиций к справочнику материалов, накопление истории цен.',
    filesHint:
      'Коммерческие предложения (КП) поставщиков с ценами на позиции. Агент извлечёт цены, зарегистрирует поставщика и привяжет позиции к справочнику материалов. Подойдёт любой формат — Excel, PDF, скан или фото.',
  },
  {
    id: 'stock',
    number: '2.4',
    phase: 2,
    title: 'Остатки и неликвиды',
    description:
      'Перенос складских остатков из 1С, подсветка неликвидов (нет движения дольше горизонта) и отрицательных остатков.',
    filesHint:
      'Выгрузка складских остатков из 1С: позиция, остаток, дата последнего движения, склад. Агент подсветит неликвиды и отрицательные остатки. Подойдёт любой формат — Excel/CSV, PDF, скан или фото.',
  },
] as const;

/** Подписи фаз агента (заголовки групп в рельсе навигации). */
export const MTO_PHASE_TITLES: Readonly<Record<MtoModulePhase, string>> = {
  1: 'Фаза 1 · Формирование потребности',
  2: 'Фаза 2 · Контроль снабжения',
};

/** Валиден ли код модуля (type guard для разбора `?module=` из URL). */
export function isMtoModuleId(value: string | null | undefined): value is MtoModuleId {
  return !!value && MTO_MODULES.some((m) => m.id === value);
}

/** Метаданные модуля по коду (для заголовка/подсказок). `null` — неизвестный код. */
export function mtoModule(module: string | null | undefined): MtoModuleOption | null {
  if (!module) return null;
  return MTO_MODULES.find((m) => m.id === module) ?? null;
}

/** Конфигурация документов для выбранного модуля обработки. */
export function moduleDocTypes(module: MtoModuleId): readonly MtoDocType[] {
  switch (module) {
    case 'rd_extract':
      return MTO_RD_DOCTYPES;
    case 'reconcile':
      return MTO_RECONCILE_DOCTYPES;
    case 'supply':
      return MTO_SUPPLY_DOCTYPES;
    case 'long_lead':
      return MTO_LONGLEAD_DOCTYPES;
    case 'suppliers':
      return MTO_SUPPLIERS_DOCTYPES;
    case 'stock':
      return MTO_STOCK_DOCTYPES;
    default:
      return MTO_PASSPORT_DOCTYPES;
  }
}

/** Человекочитаемая подпись модуля по коду (неизвестный → сам код). */
export function moduleTitle(module: string | null | undefined): string {
  if (!module) return '—';
  return MTO_MODULES.find((m) => m.id === module)?.title ?? module;
}

/* ── Хелперы статусов / тонов / подписей (по образцу economic/types) ───────── */

/** Вернуть подпись из мапы, иначе сам код (graceful fallback на новые коды). */
function labelOr(map: Readonly<Record<string, string>>, code: string): string {
  return map[code] ?? code;
}

/** Статус прогона активный (имеет смысл polling)? */
export function isMtoRunActive(status: string): boolean {
  return status === 'created' || status === 'processing';
}

/** Тон Badge для статуса прогона. */
export function statusTone(status: string): BadgeTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'processing':
      return 'info';
    case 'created':
      return 'warning';
    case 'failed':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись статуса прогона. */
export function statusLabel(status: string): string {
  switch (status) {
    case 'completed':
      return 'готово';
    case 'processing':
      return 'обработка…';
    case 'created':
      return 'в очереди';
    case 'failed':
      return 'ошибка';
    default:
      return status;
  }
}

/** Подпись этапа прогона для полосы прогресса (эвристика по проценту — точного
 *  stage-кода в контракте нет; этапы свои у каждого модуля). Терминальные
 *  статусы — свой текст у вызывающего. */
export function runStageLabel(
  status: string,
  progressPercent: number,
  module?: string | null,
): string {
  if (status === 'created') return 'В очереди на обработку';
  if (status === 'failed') return 'Обработка прервана';
  if (status === 'completed') return 'Обработка завершена';
  const p = Math.max(0, Math.min(100, progressPercent));
  if (p < 5) return 'Подготовка документов';
  switch (module) {
    case 'rd_extract':
      if (p < 90) return 'Сбор ведомостей документов РД';
      return 'Сопоставление со справочником 1С';
    case 'reconcile':
      if (p < 45) return 'Потребность по РД';
      if (p < 80) return 'Чтение заявок';
      return 'Сверка позиций';
    case 'passports':
    case null:
    case undefined:
      if (p < 45) return 'Чтение реализаций и протоколов';
      if (p < 70) return 'Сопоставление партий с протоколами';
      if (p < 90) return 'Сборка паспортов качества';
      return 'Завершение обработки';
    default:
      if (p < 90) return 'Чтение документов';
      return 'Завершение обработки';
  }
}

/** Тон Badge для статуса сопоставления партии с протоколом. */
export function matchStatusTone(matchStatus: string): BadgeTone {
  switch (matchStatus) {
    case 'matched':
      return 'success';
    case 'ambiguous':
      return 'warning';
    case 'unmatched':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись статуса сопоставления. */
export function matchStatusLabel(matchStatus: string): string {
  switch (matchStatus) {
    case 'matched':
      return 'сопоставлен';
    case 'ambiguous':
      return 'неоднозначно';
    case 'unmatched':
      return 'без протокола';
    default:
      return matchStatus;
  }
}

/** Тон Badge для статуса паспорта. */
export function passportStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'rejected':
      return 'neutral';
    case 'draft':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись статуса паспорта. */
export function passportStatusLabel(status: string): string {
  switch (status) {
    case 'confirmed':
      return 'подтверждён';
    case 'rejected':
      return 'отклонён';
    case 'draft':
      return 'черновик';
    default:
      return status;
  }
}

/** Тон Badge для критичности аномалии (как в econ). */
export function severityTone(severity: string): BadgeTone {
  switch (severity) {
    case 'critical':
      return 'danger';
    case 'major':
      return 'warning';
    case 'minor':
      return 'info';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись критичности аномалии. */
export function severityLabel(severity: string): string {
  switch (severity) {
    case 'critical':
      return 'критический';
    case 'major':
      return 'важный';
    case 'minor':
      return 'незначительный';
    default:
      return severity;
  }
}

/** Тон Badge для решения HITL (прогон/паспорт/аномалия). Покрывает решения всех
 *  трёх уровней: accepted/acknowledged → success, rejected → нейтраль,
 *  disputed → warning. */
export function decisionTone(decision: string): BadgeTone {
  switch (decision) {
    case 'accepted':
    case 'acknowledged':
      return 'success';
    case 'rejected':
      return 'neutral';
    case 'disputed':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись решения HITL. */
export function decisionLabel(decision: string): string {
  switch (decision) {
    case 'accepted':
      return 'утверждён';
    case 'acknowledged':
      return 'учтено';
    case 'rejected':
      return 'отклонён';
    case 'disputed':
      return 'спорно';
    default:
      return decision;
  }
}

/** Человекочитаемая подпись роли источника позиции потребности (1.2). Источник
 *  `rd` — текст РД, `rd_table` — таблица спецификации/ВОР, `rd_geometry` —
 *  геометрия чертежа. Неизвестная роль возвращается как есть. */
export function materialSourceLabel(role: string): string {
  switch (role) {
    case 'rd_table':
      return 'таблица';
    case 'rd_geometry':
      return 'геометрия';
    case 'rd':
      return 'текст';
    default:
      return role;
  }
}

/** Подпись источника позиции для модуля 1.4 «Сверка РД ↔ заявки». В отличие от
 *  [`materialSourceLabel`] (где `rd_table` — способ извлечения «таблица»), здесь
 *  роль различает ИСТОЧНИК сверки: `rd_table` — позиция потребности по РД,
 *  `request_table` — позиция заявки на закупку. Неизвестная роль — как есть. */
export function materialSourceRoleLabel(role: string): string {
  switch (role) {
    case 'rd_table':
      return 'РД (потребность)';
    case 'request_table':
      return 'Заявка';
    default:
      return role;
  }
}

/** Человекочитаемая подпись типа совпадения с НСИ (модуль 1.3). Неизвестный
 *  тип возвращается как есть (graceful fallback на новые типы). */
export function matchTypeLabel(matchType: string): string {
  switch (matchType) {
    case 'exact':
      return 'точное';
    case 'fuzzy':
      return 'по смыслу';
    case 'text':
      return 'по тексту';
    case 'none':
      return 'не найдено';
    default:
      return matchType;
  }
}

/** Человекочитаемая подпись статуса сопоставления с НСИ (модуль 1.3). */
export function mappingStatusLabel(status: string): string {
  switch (status) {
    case 'auto':
      return 'авто';
    case 'verified':
      return 'подтверждено';
    case 'pending':
      return 'на проверку';
    case 'rejected':
      return 'отклонено';
    default:
      return status;
  }
}

/** Тон Badge для статуса сопоставления с НСИ (модуль 1.3). Авто/подтверждено —
 *  success, на проверку — warning, отклонено — нейтраль. */
export function mappingStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'auto':
    case 'verified':
      return 'success';
    case 'pending':
      return 'warning';
    case 'rejected':
      return 'neutral';
    default:
      return 'neutral';
  }
}

/**
 * Число критических аномалий БЕЗ разбора. Критическая считается «разобранной»
 * при любом решении (`acknowledged`/`rejected`).
 */
export function unresolvedCriticalCount(anomalies: readonly MtoAnomaly[]): number {
  return anomalies.filter((a) => a.severity === 'critical' && !a.decision).length;
}

/**
 * Сколько критических замечаний блокирует утверждение. Блокируют только у
 * паспортов качества: по партии без протокола паспорт не собрать. В остальных
 * модулях замечания — сигналы для снабженца, а не ошибка разбора (раньше 28
 * «не заказано» без заявок не давали утвердить сверку). Правило зеркалит
 * backend (409 только для паспортов).
 */
export function approvalBlockingCount(
  module: string | null | undefined,
  anomalies: readonly MtoAnomaly[],
): number {
  return module === 'passports' ? unresolvedCriticalCount(anomalies) : 0;
}

/* ── Подписи показателей качества (ключи `indicators`) ─────────────────────── */

/** Русские подписи кодов показателей качества песка (ГОСТ 8736). Карта неполна
 *  по дизайну: НЕИЗВЕСТНЫЙ код возвращается как есть — новые показатели на
 *  backend не ломают вывод. */
const INDICATOR_LABELS: Readonly<Record<string, string>> = {
  fineness_modulus: 'Модуль крупности',
  dust_clay_pct: 'Содержание пылевидных и глинистых частиц, %',
  clay_lumps_pct: 'Содержание глины в комках, %',
  bulk_density: 'Насыпная плотность',
  grains_over_5mm_pct: 'Зёрна крупнее 5 мм, %',
  grains_over_10mm_pct: 'Зёрна крупнее 10 мм, %',
  moisture_pct: 'Влажность, %',
  filtration_coef: 'Коэффициент фильтрации',
  radioactivity: 'Удельная эфф. активность (класс)',
};

/** Человекочитаемая подпись показателя качества (неизвестный → сам код). */
export function labelIndicator(code: string): string {
  return labelOr(INDICATOR_LABELS, code);
}

/* ── Форматирование ────────────────────────────────────────────────────────── */

/** Дата-время `YYYY-MM-DDTHH:mm:ss` → `16.06.2026, 14:30`. Невалидное — как есть. */
export function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Уверенность извлечения 0..1 → проценты (`87%`), `null`/undefined → `—`.
 * Аналог `fmtConf` из `pages/Pto/PtoVolumes` — единый формат показа доверия
 * к авто-извлечённой позиции потребности.
 */
export function fmtConfidence(c: number | null | undefined): string {
  if (c === null || c === undefined) return '—';
  return `${Math.round(c * 100)}%`;
}

/**
 * Дата → `16.06.2026`. `null` — прочерк, нераспознанное («в течение недели») —
 * как есть.
 *
 * Даты из документов приходят текстом «как в документе» — `ДД.ММ.ГГГГ`, их
 * нельзя отдавать в `new Date`: он читает их по-американски (ММ.ДД), и срок
 * «01.11.2026» показывался как 11 января. Дату без часового пояса
 * (`2026-10-05`, `2026-10-05 00:00:00`) тоже разбираем сами — иначе западнее
 * UTC она уезжает на день назад.
 */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const v = value.trim();
  const ru = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(v);
  if (ru) {
    const [, d, m, y] = ru;
    return Number(d) >= 1 && Number(d) <= 31 && Number(m) >= 1 && Number(m) <= 12
      ? `${d.padStart(2, '0')}.${m.padStart(2, '0')}.${y}`
      : value;
  }
  const iso = /^(\d{4})-(\d{2})-(\d{2})(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/.exec(v);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;
  // Метка времени с поясом (`…T12:33:03Z`) — дата в местном времени.
  if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
    const d = new Date(v);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
  }
  return value;
}

/**
 * Разобранная цена для сводных чисел сравнения: «98 400», «1 190,5».
 *
 * Только для величин, которые СЧИТАЕТ backend (минимум, максимум, цена решения).
 * В ячейке предложения печатается `price` — он перенос из документа 1:1, и
 * подменять его округлённым числом нельзя: снабженец сверяет таблицу с КП.
 */
export function fmtPriceValue(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

/** Разброс цен: «7,0%». `null` — сравнивать нечего. */
export function fmtPercent(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${v.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
}

/** Изменение со знаком: «+6,2%», «−3,1%». Знак обязателен — без него рост и
 *  падение цены на витрине неразличимы. */
export function fmtPercentDelta(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const body = Math.abs(v).toLocaleString('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  if (v > 0) return `+${body}%`;
  // Минус — типографский (U+2212), как в остальных числовых колонках раздела.
  if (v < 0) return `−${body}%`;
  return `${body}%`;
}

/**
 * Сообщение ошибки из тела ответа API (`{"error":{"code","message"}}`). Backend
 * раздела МТО отдаёт внятные русские причины (409 «разберите критические
 * аномалии…», «недопустимый тип документа»), которые нельзя глотать общей
 * фразой — показываем как есть. `null`, если тело не в этом формате.
 */
export function apiErrorMessage(err: unknown): string | null {
  if (typeof err !== 'object' || err === null || !('data' in err)) return null;
  const data = (err as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null || !('error' in data)) return null;
  const e = (data as { error?: unknown }).error;
  if (typeof e !== 'object' || e === null || !('message' in e)) return null;
  const m = (e as { message?: unknown }).message;
  return typeof m === 'string' && m.trim() ? m : null;
}

/* ───────────────────────────────────────────────────────────────────────────
 * Подфункция «Документы качества для АОСР» (AI-MTO-2026-QC, этап 1).
 *
 * Прослеживаемость материалов для актов освидетельствования скрытых работ:
 * реестр партий материала ([`MaterialLot`]) → документы качества по партии
 * ([`QualityDoc`]: паспорт/сертификат/декларация) → факты применения партии на
 * объекте ([`LotApplication`]). Два ДЕТЕРМИНИРОВАННЫХ (без ИИ) отчёта:
 * действительность документа на дату работ (МТОК-4, [`Validity`]) и дефицит
 * «применено, но нет/просрочен документ» (МТОК-6, [`DeficitRow`]).
 *
 * Типы — зеркало DTO `/agents/mto/quality/*`, snake_case 1:1 с backend. Даты и
 * количества backend отдаёт СТРОКОЙ (текст реквизитов, не теряем точность) — на
 * фронте принимаем как `string`, не приводим к number/Date.
 * ──────────────────────────────────────────────────────────────────────────── */

/** Тип документа качества по партии. Подпись — [`docTypeLabel`]. */
export type QualityDocType = 'passport' | 'certificate' | 'declaration';

/** Статус документа качества: `confirmed` подтверждён снабженцем, `draft` —
 *  черновик (требует подтверждения). Тон/подпись — [`qualityDocStatusTone`]/
 *  [`qualityDocStatusLabel`]. */
export type QualityDocStatus = 'confirmed' | 'draft';

/** HITL по документу качества: подтвердить (`confirmed`) | вернуть в черновик
 *  (`draft`). */
export type QualityDocDecision = 'confirmed' | 'draft';

/** Вердикт действительности документа качества на дату работ (МТОК-4). Считается
 *  детерминированно (проверкой срока действия), без модели. Тон/подпись —
 *  [`validityTone`]/[`validityLabel`]. */
export type Validity = 'perpetual' | 'valid' | 'expired' | 'unknown';

/** Тип дефицита по применённой партии (МТОК-6). Тон/подпись —
 *  [`deficitKindTone`]/[`deficitKindLabel`]. */
export type DeficitKind = 'no_document' | 'expired' | 'unverifiable' | 'unconfirmed';

/** Партия материала (реестр прослеживаемости). Количество и даты — СТРОКА
 *  (текст реквизитов), не number/Date; `site_id` — привязка к объекту. */
export interface MaterialLot {
  id: string;
  lot_no: string | null;
  nomenclature: string;
  supplier: string | null;
  supply_date: string | null;
  quantity: string | null;
  unit: string | null;
  site_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Документ качества по партии (паспорт/сертификат/декларация). `has_file` —
 *  приложен ли файл (PDF/фото) для скачивания (`file_content` в листинге не
 *  выбирается); даты — строкой. */
export interface QualityDoc {
  id: string;
  lot_id: string;
  doc_type: QualityDocType;
  number: string | null;
  doc_date: string | null;
  issuer: string | null;
  valid_until: string | null;
  nomenclature: string | null;
  supply_ref: string | null;
  site_id: string | null;
  file_name: string | null;
  has_file: boolean;
  status: QualityDocStatus;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Факт применения партии на объекте (что и куда уложено). `applied_date` —
 *  дата применения строкой; `element_id`/`applied_fact_ref` — привязка к
 *  элементу/факту работ. */
export interface LotApplication {
  id: string;
  lot_id: string;
  site_id: string | null;
  element_id: string | null;
  applied_fact_ref: string | null;
  applied_date: string | null;
  created_by: string | null;
  created_at: string;
}

/** Документ качества с вердиктом действительности на дату работ (МТОК-4). */
export interface QualityDocValidity {
  doc: QualityDoc;
  validity: Validity;
}

/** Строка отчёта дефицита (МТОК-6): применённая партия без действительного
 *  документа качества. `doc_count` — сколько документов у партии всего. */
export interface DeficitRow {
  application: LotApplication;
  lot_no: string | null;
  nomenclature: string | null;
  doc_count: number;
  kind: DeficitKind;
}

/** Тело создания партии (JSON). `nomenclature` обязательно; прочее опционально. */
export interface NewMaterialLot {
  lot_no?: string;
  nomenclature: string;
  supplier?: string;
  supply_date?: string;
  quantity?: string;
  unit?: string;
  site_id?: string;
}

/** Тело создания факта применения партии (JSON). `lot_id` обязателен. */
export interface NewLotApplication {
  lot_id: string;
  site_id?: string;
  element_id?: string;
  applied_fact_ref?: string;
  applied_date?: string;
}

/** Реквизиты, извлечённые моделью из документа качества (МТОК-2, зеркало
 *  `ExtractedQualityDoc`). Все поля опциональны: чего нет в тексте — `null`.
 *  `doc_type` нормализован к `passport|certificate|declaration` (или `null`,
 *  если тип не распознан). `notes` — диагностика/пометки модели. */
export interface ExtractedQualityDoc {
  doc_type: string | null;
  number: string | null;
  doc_date: string | null;
  valid_until: string | null;
  issuer: string | null;
  nomenclature: string | null;
  lot_no: string | null;
  gost: string | null;
  notes: string | null;
  /** Документ не о качестве партии («паспорт безопасности», «свидетельство о
   *  госрегистрации (СГР)»): для актов скрытых работ не нужен, при загрузке
   *  пачки строка по умолчанию пропускается. */
  not_quality?: string | null;
}

/** Подсказка привязки извлечённого документа к существующей партии. `matched_by`
 *  — по чему подобрано: `lot_no` (точный № партии) | `nomenclature` (единственная
 *  партия с таким наименованием; слабее — стоит перепроверить). */
export interface SuggestedLot {
  id: string;
  label: string;
  matched_by: 'lot_no' | 'nomenclature';
}

/** Элемент предпросмотра массовой загрузки документов качества: реквизиты из
 *  файла + подсказка партии. `index` — позиция файла во входной пачке (фронт
 *  сопоставляет по нему свой `File`). `error` — файл не прочитан/не извлечён
 *  (отказ одного файла не роняет пачку). Ничего не сохранено — импорт идёт
 *  штатными эндпоинтами после правки. */
export interface QualityBatchItem {
  /** Индекс файла во входной пачке. */
  index: number;
  /** Документ в файле (с 1) и сколько их в файле: пачка паспортов в одном PDF
   *  делится по страницам; один документ — 1 из 1. */
  part: number;
  parts: number;
  /** Страницы документа в файле `[первая, последняя]` — только у пачки. */
  pages: [number, number] | null;
  filename: string;
  extracted: ExtractedQualityDoc;
  suggested_lot: SuggestedLot | null;
  error: string | null;
}

/* ── Лейблы / тоны (по образцу хелперов выше) ──────────────────────────────── */

const QUALITY_DOC_TYPE_LABELS: Readonly<Record<QualityDocType, string>> = {
  passport: 'Паспорт',
  certificate: 'Сертификат',
  declaration: 'Декларация соответствия',
};

/** Человекочитаемая подпись типа документа качества (неизвестный → сам код). */
export function docTypeLabel(docType: string): string {
  return QUALITY_DOC_TYPE_LABELS[docType as QualityDocType] ?? docType;
}

/** Все типы документа качества для select-формы (значение + подпись). */
export const QUALITY_DOC_TYPES: ReadonlyArray<{ value: QualityDocType; label: string }> = [
  { value: 'passport', label: QUALITY_DOC_TYPE_LABELS.passport },
  { value: 'certificate', label: QUALITY_DOC_TYPE_LABELS.certificate },
  { value: 'declaration', label: QUALITY_DOC_TYPE_LABELS.declaration },
];

/** Тон Badge для статуса документа качества: подтверждён — success, черновик —
 *  warning (требует подтверждения). */
export function qualityDocStatusTone(status: string): BadgeTone {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'draft':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись статуса документа качества. */
export function qualityDocStatusLabel(status: string): string {
  switch (status) {
    case 'confirmed':
      return 'подтверждён';
    case 'draft':
      return 'черновик';
    default:
      return status;
  }
}

/** Тон Badge для вердикта действительности (МТОК-4). Бессрочный/действителен —
 *  success, просрочен — danger, срок не распознан — warning. */
export function validityTone(validity: string): BadgeTone {
  switch (validity) {
    case 'perpetual':
    case 'valid':
      return 'success';
    case 'expired':
      return 'danger';
    case 'unknown':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись вердикта действительности (МТОК-4). */
export function validityLabel(validity: string): string {
  switch (validity) {
    case 'perpetual':
      return 'Бессрочный';
    case 'valid':
      return 'Действителен';
    case 'expired':
      return 'Просрочен';
    case 'unknown':
      return 'Срок не распознан';
    default:
      return validity;
  }
}

/** Тон Badge для типа дефицита (МТОК-6). Нет документа/просрочен — danger,
 *  срок не подтверждён и черновик без проверки — warning. */
export function deficitKindTone(kind: string): BadgeTone {
  switch (kind) {
    case 'no_document':
    case 'expired':
      return 'danger';
    case 'unverifiable':
    case 'unconfirmed':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** Человекочитаемая подпись типа дефицита (МТОК-6). */
export function deficitKindLabel(kind: string): string {
  switch (kind) {
    case 'no_document':
      return 'Нет документа качества';
    case 'expired':
      return 'Документ просрочен';
    case 'unverifiable':
      return 'Срок не подтверждён';
    case 'unconfirmed':
      return 'Документ не подтверждён';
    default:
      return kind;
  }
}

/** Сегодняшняя дата в формате `YYYY-MM-DD` (для дефолта поля «дата работ»). */
export function todayIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}
